import {
  objectParser,
  nullable,
  exactObject,
  literalAt,
  type Parser,
  arrayAt,
  identifierAt,
  nullableAt,
  arrayOf,
  assertUnique,
  booleanAt,
  webOriginAt
} from './primitives';
import {
  type BootstrapSnapshot,
  type SidebarDestination,
  type BridgeCapabilities,
  type RetryAdviceSettings
} from '../dtos';
import {
  parseEnvironment,
  parseAuthState,
  parseTabContext,
  parseStateRevisions,
  parseSmartFillUiPreferences,
  settingsUpdatePayloadAt,
  openCaraPayloadAt
} from './context';
import {
  parseFillSource,
  listPayloadAt,
  parseBucketListEntry,
  bucketGetPayloadAt,
  parseSmartFillBucket,
  bucketCreatePayloadAt,
  bucketUpdatePayloadAt,
  bucketDeletePayloadAt,
  bucketRevertLearningPayloadAt,
  workspaceListPayloadAt,
  parseWorkspace,
  workspaceUsersAt,
  workspaceConvertPayloadAt,
  parseWorkspaceConversion,
  parseBucketProfile,
  activeBucketSetPayloadAt
} from './buckets';
import {
  parsePageSessionSnapshot,
  fillStartOncePayloadAt,
  fillStartContinuousPayloadAt,
  focusReviewFieldPayloadAt,
  saveReviewEditPayloadAt,
  parseLearnedRule,
  ruleClearPayloadAt
} from './page-session';
import { parseSuperCopySnapshot, parseSuperCopySavePayload } from './supercopy';
import {
  type NoPayload,
  type BucketDeleteResult,
  type FocusReviewFieldResult,
  type RuleClearResult,
  type OpenCaraResult,
  type BridgeCommand,
  type BridgeEvent,
  type RetryAdviceSettingsUpdatePayload
} from '../protocol';
import {
  parseLearningSettings,
  parseLearningSettingsUpdate,
  parseLearningSnapshot,
  learningEntriesPayloadAt
} from './learning';

export const parseBootstrapSnapshot = objectParser<BootstrapSnapshot>({
  environment: parseEnvironment,
  auth: parseAuthState,
  tab: parseTabContext,
  activeBucket: nullable(parseFillSource),
  pageSession: parsePageSessionSnapshot,
  superCopy: parseSuperCopySnapshot,
  stateRevisions: parseStateRevisions
});

const noPayloadAt = objectParser<NoPayload>({});

const capabilitiesAt = objectParser<BridgeCapabilities>(
  { capabilities: arrayOf(identifierAt, 32) },
  {
    refine: (object, path) =>
      assertUnique(
        object.capabilities as string[],
        (item) => item,
        `${path}.capabilities`,
        'capability'
      )
  }
);
const retryAdviceSettingsAt = objectParser<RetryAdviceSettings>({
  enabled: booleanAt,
  siteEnabled: booleanAt,
  origin: nullable(webOriginAt)
});
const retryAdviceSettingsUpdateAt = objectParser<RetryAdviceSettingsUpdatePayload>(
  {},
  { optional: { enabled: booleanAt, siteEnabled: booleanAt } }
);

function literalResultAt<T extends string>(value: unknown, path: string, key: T): Record<T, true> {
  const object = exactObject(value, path, [key]);
  literalAt(object[key], true, `${path}.${key}`);
  return value as Record<T, true>;
}

interface RuntimeRequestDefinition {
  payload: Parser<unknown>;
  result: Parser<unknown>;
}

export const REQUEST_VALIDATORS = {
  bootstrap: { payload: noPayloadAt, result: parseBootstrapSnapshot },
  'capabilities.get': { payload: noPayloadAt, result: capabilitiesAt },
  'buckets.list': {
    payload: listPayloadAt,
    result: (value, path) => arrayAt(value, path, parseBucketListEntry)
  },
  'buckets.get': { payload: bucketGetPayloadAt, result: parseSmartFillBucket },
  'buckets.create': { payload: bucketCreatePayloadAt, result: parseSmartFillBucket },
  'buckets.update': { payload: bucketUpdatePayloadAt, result: parseSmartFillBucket },
  'buckets.delete': {
    payload: bucketDeletePayloadAt,
    result: (value, path) => literalResultAt(value, path, 'deleted') as BucketDeleteResult
  },
  'buckets.revertLearning': {
    payload: bucketRevertLearningPayloadAt,
    result: parseSmartFillBucket
  },
  'workspaces.list': {
    payload: workspaceListPayloadAt,
    result: (value, path) => arrayAt(value, path, parseWorkspace)
  },
  'workspaces.users': { payload: noPayloadAt, result: workspaceUsersAt },
  'workspaces.buckets': {
    payload: objectParser({ workspaceId: identifierAt }),
    result: (value, path) => arrayAt(value, path, parseBucketListEntry)
  },
  'workspaces.convert': {
    payload: workspaceConvertPayloadAt,
    result: parseWorkspaceConversion
  },
  'workspaces.convertAcord': {
    payload: workspaceConvertPayloadAt,
    result: parseWorkspaceConversion
  },
  'profiles.list': {
    payload: noPayloadAt,
    result: (value, path) => arrayAt(value, path, parseBucketProfile)
  },
  'settings.get': { payload: noPayloadAt, result: parseSmartFillUiPreferences },
  'settings.update': {
    payload: settingsUpdatePayloadAt,
    result: parseSmartFillUiPreferences
  },
  'learning.settings.get': { payload: noPayloadAt, result: parseLearningSettings },
  'learning.settings.update': {
    payload: parseLearningSettingsUpdate,
    result: parseLearningSettings
  },
  'learning.get': { payload: noPayloadAt, result: parseLearningSnapshot },
  'learning.stop': { payload: noPayloadAt, result: parseLearningSnapshot },
  'learning.finish': { payload: noPayloadAt, result: parseLearningSnapshot },
  'learning.apply': { payload: learningEntriesPayloadAt, result: parseLearningSnapshot },
  'learning.undo': { payload: learningEntriesPayloadAt, result: parseLearningSnapshot },
  'retryAdvice.settings.get': { payload: noPayloadAt, result: retryAdviceSettingsAt },
  'retryAdvice.settings.update': {
    payload: retryAdviceSettingsUpdateAt,
    result: retryAdviceSettingsAt
  },
  'activeBucket.set': {
    payload: activeBucketSetPayloadAt,
    result: (value, path) => nullableAt(value, path, parseFillSource)
  },
  'fill.startOnce': { payload: fillStartOncePayloadAt, result: parsePageSessionSnapshot },
  'fill.startContinuous': {
    payload: fillStartContinuousPayloadAt,
    result: parsePageSessionSnapshot
  },
  'fill.continueContinuous': { payload: noPayloadAt, result: parsePageSessionSnapshot },
  'fill.finishContinuous': { payload: noPayloadAt, result: parsePageSessionSnapshot },
  'review.focusField': {
    payload: focusReviewFieldPayloadAt,
    result: (value, path) => literalResultAt(value, path, 'focused') as FocusReviewFieldResult
  },
  'review.saveEdit': {
    payload: saveReviewEditPayloadAt,
    result: parsePageSessionSnapshot
  },
  'rules.list': {
    payload: noPayloadAt,
    result: (value, path) => arrayAt(value, path, parseLearnedRule)
  },
  'rules.clear': {
    payload: ruleClearPayloadAt,
    result: (value, path) => literalResultAt(value, path, 'cleared') as RuleClearResult
  },
  'superCopy.start': { payload: noPayloadAt, result: parseSuperCopySnapshot },
  'superCopy.cancel': { payload: noPayloadAt, result: parseSuperCopySnapshot },
  'superCopy.save': { payload: parseSuperCopySavePayload, result: parseSuperCopySnapshot },
  'cara.open': {
    payload: openCaraPayloadAt,
    result: (value, path) => literalResultAt(value, path, 'opened') as OpenCaraResult
  }
} satisfies Record<BridgeCommand, RuntimeRequestDefinition>;

export const EVENT_VALIDATORS = {
  'bootstrap.updated': parseBootstrapSnapshot,
  'auth.updated': parseAuthState,
  'tab.updated': parseTabContext,
  'activeBucket.updated': (value, path) => nullableAt(value, path, parseFillSource),
  'pageSession.updated': parsePageSessionSnapshot,
  'sidebar.navigate': sidebarDestinationAt,
  'superCopy.updated': parseSuperCopySnapshot,
  'learning.changed': parseLearningSnapshot,
  'rules.changed': objectParser<NoPayload>({})
} satisfies Record<BridgeEvent, Parser<unknown>>;

function sidebarDestinationAt(value: unknown, path: string): SidebarDestination {
  if (value === 'THIS_PAGE' || value === 'WORKSPACES') return value;
  const object = exactObject(value, path, ['kind', 'workspaceId']);
  literalAt(object.kind, 'GENERATE_WORKSPACE', `${path}.kind`);
  identifierAt(object.workspaceId, `${path}.workspaceId`);
  return value as SidebarDestination;
}
